import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { graphql, useStaticQuery } from 'gatsby';
import { motion } from 'framer-motion';
import StackGrid, { transitions } from 'react-stack-grid';
import { useInView } from 'react-intersection-observer';

import FeaturedProject from '../../featured-projects';
import { getFluidGatsbyImage } from '../../../utils/gatsby-storyblok-image-fixed/';
import Container from '../../container';
import Title from '../../title';
import RefreshMessage from '../../refresh-message';
import PreviewTile from '../../preview-tile';
import { Paragraph } from '../../UIKit/system';
import CaseStudiesBrowserFilter from '../../case-studies-browser-filter';
import Responsive, { withResponsiveContext } from '../../responsive';
import CaseStudiesBrowserFilterMobile from '../../case-studies-browser-filter-mobile';
import CaseStudiesSlider from '../../case-studies-slider/CaseStudiesSlider';
import CaseStudyProjectGrid from '../../case-study-project-grid/CaseStudyProjectGrid';
import { styledLink } from '../../../utils/styles';

const StyledContainer = styled(Container)`
  max-width: 1195px;
`;

const FeaturedProjectsContainer = styled(motion.div)`
  overflow: hidden;
`;

const NoItems = styled.div`
  width: 100%;
  height: 250px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  text-align: center;
  padding: 0px 10px;
`;

const StyledTitle = styled(Title)`
  text-transform: capitalize;
  margin-bottom: 6px;
`;

const StyledParagraph = styled(Paragraph)`
  text-transform: capitalize;
  margin-bottom: 30px;
  min-height: 28px;
`;

const FilterSectionContainer = styled.div``;

const FilterSectionTextContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-width: 536px;
  margin-bottom: 32px;
  margin-top: 64px;
`;

const FilterSectionTitle = styled.h2`
  color: #161616;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  letter-spacing: -0.84px;
  text-transform: capitalize;
`;

const FilterSectionDescription = styled.div`
  color: #666;
  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  letter-spacing: -0.18px;
  text-transform: capitalize;
  a {
    ${styledLink};
    text-decoration: none;
  }
`;

const StoryblokCaseStudiesBrowserAdapter = ({
  featuredProjectsTitle,
  featuredProjectsSubtitle,
  featuredProjects,
  isMobile
}) => {
  const data = useStaticQuery(graphql`
    query CaseStudiesQuery {
      caseStudies: allStoryblokEntry(
        filter: { field_component: { eq: "caseStudy" } }
      ) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      locations: allStoryblokEntry(
        filter: { field_component: { eq: "location" } }
      ) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
      properties: allStoryblokEntry(
        filter: { field_component: { eq: "propertyType" } }
      ) {
        edges {
          node {
            name
            full_slug
            content
          }
        }
      }
    }
  `);

  const [relationFailed, setrelationFailed] = useState(false);

  const { scaleDown, scaleUp } = transitions;

  const [isInView, setIsInView] = useState(false);

  const [ref, inView] = useInView({
    threshold: 0
  });

  useEffect(() => {
    setTimeout(() => {
      setIsInView(true);
    }, 10);
  }, []);

  const [filters, setFilters] = useState({
    projectType: '',
    propertyType: '',
    location: ''
  });

  function normalizeLower(value) {
    return typeof value === 'string' ? value.toLowerCase() : '';
  }

  let featuredCaseStudies = [];
  featuredProjects.forEach((project) => {
    if (!relationFailed) {
      if (project.content !== undefined) {
        let fluidProjectImage = getFluidGatsbyImage(project.content.gridImage, {
          maxWidth: 1080
        });

        let parsedProject = {
          title: project.name,
          image: fluidProjectImage,
          to: '/' + project.full_slug
        };
        featuredCaseStudies.push(parsedProject);
      } else {
        setrelationFailed(true);
      }
    }
  });

  let allCaseStudies = [];

  if (!data?.caseStudies?.edges) return;

  data?.caseStudies?.edges?.forEach((project) => {
    let projectData = JSON.parse(project.node.content);
    if (!projectData) return;
    let fluidProjectImage;
    if (projectData !== 'undefined' && projectData.gridImage !== 'undefined') {
      fluidProjectImage = getFluidGatsbyImage(projectData.gridImage, {
        maxWidth: 1080
      });
    } else {
      fluidProjectImage = null;
    }

    let parsedProject = {
      title: project.node.name,
      image: fluidProjectImage,
      to: '/' + project.node.full_slug,
      location: normalizeLower(projectData?.location?.name),
      projectType: normalizeLower(projectData?.projectType),
      propertyType: normalizeLower(projectData?.propertyType?.name),
      content: projectData,
      full_slug: project.node.full_slug
    };
    allCaseStudies.push(parsedProject);
  });

  function handleFilterUpdate(key, value) {
    const keyUpd = key;
    const valueUpd = value;
    setFilters((state) => ({
      ...state,
      [keyUpd]: valueUpd
    }));
  }

  function filtersSelected(filters) {
    return Object.keys(filters).some((key) => !!filters[key]);
  }

  function renderFiltered() {
    const selectedFilterKeys = Object.keys(filters).filter(
      (key) => !!filters[key]
    );
    const filteredItems = allCaseStudies.filter((v) => {
      return selectedFilterKeys.every((key) => v[key] === filters[key]);
    });

    if (filteredItems.length === 0) {
      return (
        <NoItems>
          <div>
            No items matched your filtering - try removing some filters.
          </div>
        </NoItems>
      );
    }

    let projectTypeTitle;
    let projectSubtitle;

    if (filters.projectType === '') {
      projectTypeTitle = 'All Projects';
    } else {
      projectTypeTitle = filters.projectType + ' Projects';
    }

    if (filters.propertyType === '') {
      if (filters.location === '') {
        projectSubtitle = ' ';
      } else {
        projectSubtitle = 'In ' + filters.location;
      }
    } else {
      if (filters.location === '') {
        projectSubtitle = filters.propertyType + ' Buildings';
      } else {
        projectSubtitle =
          filters.propertyType + ' Buildings in ' + filters.location;
      }
    }

    return (
      <FilterSectionContainer>
        <Container>
          <FilterSectionTextContainer>
            <FilterSectionTitle>{projectTypeTitle}</FilterSectionTitle>
            <FilterSectionDescription>
              {projectSubtitle}
            </FilterSectionDescription>
          </FilterSectionTextContainer>
        </Container>
        <CaseStudyProjectGrid caseStudies={filteredItems} />
      </FilterSectionContainer>
    );
  }

  function getGutterWidth() {
    if (isMobile) {
      return 14;
    }

    return 26;
  }

  const gutterWidth = getGutterWidth();

  return (
    <React.Fragment>
      <div ref={ref}>
        {isInView && (
          <>
            <Responsive.DesktopOrTablet>
              <CaseStudiesBrowserFilter
                projectType={filters.projectType}
                locations={data.locations}
                location={filters.location}
                propertyTypes={data.properties}
                propertyType={filters.propertyType}
                handleFilterUpdate={handleFilterUpdate}
              />
            </Responsive.DesktopOrTablet>
            <Responsive.Mobile>
              <CaseStudiesBrowserFilterMobile
                projectType={filters.projectType}
                locations={data.locations}
                location={filters.location}
                propertyTypes={data.properties}
                propertyType={filters.propertyType}
                handleFilterUpdate={handleFilterUpdate}
                setFilters={setFilters}
              />
            </Responsive.Mobile>
          </>
        )}
      </div>

      {filtersSelected(filters) ? (
        renderFiltered()
      ) : (
        <div ref={ref}>
          {isInView && (
            <>
              {/*<FeaturedProjectsContainer>
                <FeaturedProject
                  pt={[80, 103]}
                  mb={[80, 130, 156]}
                  title={featuredProjectsTitle}
                  subtitle={featuredProjectsSubtitle}
                  items={featuredCaseStudies}
                />
                {relationFailed ? <RefreshMessage /> : null}
              </FeaturedProjectsContainer>

              <StyledContainer>
                  <Title mb={40} fontSizeMedium textAlign={'center'}>
                  All Projects
                </Title>
                <StackGrid
                  columnWidth={isMobile ? '100%' : '33.33%'}
                  gutterWidth={gutterWidth}
                  gutterHeight={gutterWidth}
                  appear={scaleUp.appear}
                  appeared={scaleUp.appeared}
                  enter={scaleUp.enter}
                  entered={scaleUp.entered}
                  leaved={scaleDown.leaved}
                >
                  {allCaseStudies.map((data, i) => (
                    <PreviewTile key={i} {...data} gatsbyImg />
                  ))}
                </StackGrid>
              </StyledContainer>*/}
              <CaseStudiesSlider
                sectionTitle={featuredProjectsTitle}
                sectionDescription={featuredProjectsSubtitle}
                lightTheme
                topAlignedNavigation
                caseStudies={
                  featuredProjects &&
                  featuredProjects?.length > 0 &&
                  featuredProjects?.map((project) => {
                    return {
                      title: project.content.title,
                      description: project.content.about[0].infoParagraph,
                      image: project.content.gridImage,
                      ctaLabel: 'View Case Study',
                      ctaLink: `/${project.full_slug}`,
                      infoItems: project.content.about[0].projectStats?.map(
                        (stat) => {
                          return {
                            label: stat.name,
                            value: stat.value
                          };
                        }
                      )
                    };
                  })
                }
              />

              <CaseStudyProjectGrid caseStudies={allCaseStudies} />
            </>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default withResponsiveContext(StoryblokCaseStudiesBrowserAdapter);
